// Modules
import React, { useContext, ReactElement, useEffect, useState, useRef } from 'react';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

// Components
import { ButtonPrimary, FilterRowWrapper } from '../../../common/components';
import { YourInfoBlock } from './YourInfoBlock';
import { Loading } from '../../../common/components';

// Context
import { MeterUpgradeProcessContext } from '../meterUpradeProcess/meterUpgradeProcessContext';
import { useAuthState } from '../../../providers/authProvider';

// Api
import { saveMeterUpgradeDetails } from '../../../common/api/meterUpgradeApi';

// Styles
import { MeterUpgradeWrapper, SitesWrapper } from './MeterUpgradeContactDetails.styles';

// Utils
import FormElement from '../../../common/components/FormElement';
import { UserDetailContextProps, UserDetailContext } from '../../../context/userDetail/userDetailContext';
import { SubmissionFailedModal } from '../../mymessages/modalTemplates/SubmissionFailedModal';
import { SiteContactDetails } from './SiteContactDetails';

type MeterUpgradeContactDetailsProps = {
  stepsBar: ReactElement;
  pageTitle: ReactElement;
};

type Inputs = {
  customerAccountReference: string;
  accountName: string;
  sites: Array<Api.IMeterUpgradeSiteDetails>;
  yourContactName: string;
  yourPhoneNumber: string;
  externalEmail: boolean;
};

export const MeterUpgradeContactDetails = ({ stepsBar, pageTitle }: MeterUpgradeContactDetailsProps) => {
  const history = useHistory();
  const authContext = useAuthState();
  const { selectedSites, setFilledSitesData, rawGridData } = useContext<any>(MeterUpgradeProcessContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailFormCopy, setEmailFormCopy] = useState<string>('No');
  const [sendExternalEmail, setSendExternalEmail] = useState<boolean>(false);
  const [showFailureModal, setShowFailureModal] = useState<boolean>(false);
  const [sitesData, setSitesData] = useState<Array<Api.IMeterUpgradeSiteDetails>>([]);
  const [finalContactDetails, setFinalContactDetails] = useState<Api.IMeterUpgradeAccountDetails>();

  const [submitButtonValidTouched, setSubmitButtonValidTouched] = useState<boolean>(false);

  const { userDetail } = useContext<UserDetailContextProps>(UserDetailContext);

  const selectedCustomerDetails = userDetail.customerAccounts.find(c => c.essAccountID === userDetail.essCustomerId);
  let finalAccountsData: Api.IMeterUpgradeAccountDetails;

  useEffect(() => {
    // Navigate to 1st process step in case page reload
    if (!selectedSites?.length) {
      history.push('/home/meter-upgrade');
    }
    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSites]);

  useEffect(() => {
    let selectedSitesArray = groupMetersByMpan(selectedSites);
    setSitesData(selectedSitesArray);
    handleAcountDataChange(selectedSitesArray);
  }, []);

  const sitesDefaultFormData = selectedSites.map((site: any) => {
    return {
      siteId: site['site-id'],
    };
  });

  const methods = useForm<Inputs>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      sites: sitesDefaultFormData,
      yourContactName: '',
      yourPhoneNumber: '',
      externalEmail: false,
    },
  });

  const { control, watch, handleSubmit } = methods;
  const { fields } = useFieldArray({ control, name: 'sites' });

  const contactName = watch(`yourContactName`);
  const contactNumber = watch(`yourPhoneNumber`);

  const validSupplementaryQuestions = finalContactDetails?.sites.every((s: any) => {
    let validSupplementaryQuestionDetails = true;
    if (s.supplementaryQuestions?.parkingRestrictions?.answer) {
      if (
        !s.supplementaryQuestions.parkingRestrictions.details ||
        s.supplementaryQuestions.parkingRestrictions?.details.length == 0
      )
        validSupplementaryQuestionDetails = false;
    }

    if (s.supplementaryQuestions?.accessRestrictions?.answer) {
      if (
        !s.supplementaryQuestions.accessRestrictions.details ||
        s.supplementaryQuestions.accessRestrictions?.details.length == 0
      )
        validSupplementaryQuestionDetails = false;
    }

    if (s.supplementaryQuestions?.accessAbove8ft?.answer) {
      if (
        !s.supplementaryQuestions.accessAbove8ft.details ||
        s.supplementaryQuestions.accessAbove8ft?.details.length == 0
      )
        validSupplementaryQuestionDetails = false;
    }

    if (s.supplementaryQuestions?.vulnerabilitiesOnSite?.answer) {
      if (
        !s.supplementaryQuestions.vulnerabilitiesOnSite.details ||
        s.supplementaryQuestions.vulnerabilitiesOnSite?.details.length == 0
      )
        validSupplementaryQuestionDetails = false;
    }

    return validSupplementaryQuestionDetails;
  });

  const validContactInfo = finalContactDetails?.sites.every((s: any) => {
    let validEmail = true;
    if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.contactDetails?.contactEmail) ||
      s.contactDetails?.contactEmail.length == 0
    )
      validEmail = false;

    return (
      s.contactDetails?.contactName !== undefined &&
      s.contactDetails?.contactName.length > 0 &&
      s.contactDetails?.contactNumber !== undefined &&
      s.contactDetails?.contactNumber.length >= 11 &&
      validEmail
    );
  });

  const submitButtonValid = validContactInfo && contactName !== '' && contactNumber !== '';

  const groupMetersByMpan = (data: Array<Api.IMeter>) => {
    const mapper = new Map();

    for (const item of data) {
      if (!mapper.has(item['site-reference'])) {
        mapper.set(item['site-reference'], {
          siteReference: item['site-reference'],
          siteAddress: item['site-address'],
          meters: [{ mpan: item['mpan-core'], meterSerialNumbers: [item['meter-serial-number']] }],
        });
      } else {
        const site = mapper.get(item['site-reference']);
        const existingMeter = site.meters.find((m: any) => m.mpan === item['mpan-core']);

        if (existingMeter) {
          if (!existingMeter.meterSerialNumbers.includes(item['meter-serial-number'])) {
            existingMeter.meterSerialNumbers.push(item['meter-serial-number']);
          }
        } else {
          site.meters.push({ mpan: item['mpan-core'], meterSerialNumbers: [item['meter-serial-number']] });
        }
      }
    }

    return Array.from(mapper.values());
  };

  const saveData = async (data: Inputs) => {
    const preparedData: any = {
      customerAccountReference: data.customerAccountReference,
      accountName: data.accountName,
      requestRaisedBy: {
        contactName: data.yourContactName,
        contactNumber: data.yourPhoneNumber,
        externalEmail: sendExternalEmail,
      },
    };

    const sites: Array<Api.IMeterUpgradeSiteDetails> = [];
    data.sites.map((value: Api.IMeterUpgradeSiteDetails) => {
      let site: Api.IMeterUpgradeSiteDetails = {
        siteAddress: value.siteAddress,
        meters: value.meters,
        contactDetails: {
          contactName: value.contactDetails?.contactName,
          contactNumber: value.contactDetails?.contactNumber,
          contactEmail: value.contactDetails?.contactEmail,
        },
        supplementaryQuestions: {
          parkingRestrictions: {
            answer: value.supplementaryQuestions?.parkingRestrictions?.answer,
            details: value.supplementaryQuestions?.parkingRestrictions?.details,
          },
          accessRestrictions: {
            answer: value.supplementaryQuestions?.accessRestrictions?.answer,
            details: value.supplementaryQuestions?.accessRestrictions?.details,
          },
          accessAbove8ft: {
            answer: value.supplementaryQuestions?.accessAbove8ft?.answer,
            details: value.supplementaryQuestions?.accessAbove8ft?.details,
          },
          vulnerabilitiesOnSite: {
            answer: value.supplementaryQuestions?.vulnerabilitiesOnSite?.answer,
            details: value.supplementaryQuestions?.vulnerabilitiesOnSite?.details,
          },
        },
      };
      sites.push(site);
    });
    preparedData.sites = sites;

    setIsSubmitting(true);

    const result = await saveMeterUpgradeDetails(process.env.REACT_APP_API_URL, authContext, preparedData);

    setIsSubmitting(false);

    if (result?.success) {
      history.push('/home/meter-upgrade/confirmation');
    } else {
      setShowFailureModal(true);
    }
  };

  const handleAcountDataChange = (data: any) => {
    finalAccountsData = {
      accountReference: userDetail.essCustomerId,
      accountName: selectedCustomerDetails ? selectedCustomerDetails.accountName : '',
      sites: data,
    };

    setFinalContactDetails(finalAccountsData);
  };

  const submitHandler: SubmitHandler<Inputs> = data => {
    if (finalContactDetails && finalContactDetails.sites.length > 0) {
      data.customerAccountReference = userDetail.essCustomerId;
      data.accountName = selectedCustomerDetails ? selectedCustomerDetails.accountName : '';
      data.sites = finalContactDetails.sites;

      // Update Meter Serial Numbers for each Mpan
      data.sites.map((site: Api.IMeterUpgradeSiteDetails) => {
        site.meters?.map((meter: Api.IMeterUpgradeSiteMeterDetails) => {
          const mpan = meter.mpan;
          const meterData = rawGridData.filter((data: any) => {
            return data['mpan-core'] === mpan;
          });
          const allMeterSerialNumbers = meterData.map((item: Api.IMeter) => {
            return item['meter-serial-number'];
          });
          meter.meterSerialNumbers = allMeterSerialNumbers;
        });
      });
    }

    saveData(data);
    setFilledSitesData(data);
  };

  const formRef = useRef<HTMLDivElement>(null);

  const scrollToFirstError = () => {
    setTimeout(() => {
      const firstError = formRef.current?.querySelector<HTMLElement>(
        '[aria-invalid="true"], [class*="error"], [class*="invalid"]'
      );
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const handleConfirmationStep = (event: any) => {
    event.preventDefault();

    if (!validSupplementaryQuestions) {
      setSubmitButtonValidTouched(true);
      scrollToFirstError();
      return;
    }

    handleSubmit(submitHandler)();
  };

  const handleUpdateContactName = (name: string, index: number) => {
    const updatedContactDetails: Api.IMeterUpgradeContactDetails = {
      ...finalContactDetails?.sites[index].contactDetails,
      contactName: name,
    };
    handleUpdateSiteDetailsLevel('contactDetails', updatedContactDetails, index);
  };

  const handleUpdateContactNumber = (number: string, index: number) => {
    const updatedContactDetails: Api.IMeterUpgradeContactDetails = {
      ...finalContactDetails?.sites[index].contactDetails,
      contactNumber: number,
    };
    handleUpdateSiteDetailsLevel('contactDetails', updatedContactDetails, index);
  };

  const handleUpdateContactEmail = (email: string, index: number) => {
    const updatedContactDetails: Api.IMeterUpgradeContactDetails = {
      ...finalContactDetails?.sites[index].contactDetails,
      contactEmail: email,
    };
    handleUpdateSiteDetailsLevel('contactDetails', updatedContactDetails, index);
  };

  const handleUpdateSiteDetailsLevel = (updatedField: string, updatedDetails: any, index: number) => {
    const updatedIndividualSiteDetails: Api.IMeterUpgradeSiteDetails = {
      ...finalContactDetails?.sites[index],
      [updatedField]: updatedDetails,
    };
    const updatedSitesDetails: Api.IMeterUpgradeSiteDetails[] =
      finalContactDetails?.sites.map((c, i) => {
        if (i === index) {
          return updatedIndividualSiteDetails;
        } else {
          return c;
        }
      }) || [];

    setFinalContactDetails({ ...finalContactDetails, sites: updatedSitesDetails });
  };

  const handleUpdateParkingRestrictionRadio = (response: string, index: number) => {
    const reponseBool = response === 'Yes' ? true : false;

    const updatedParkingRestrictionReponse: Api.IMeterUpgradeAnswer = {
      ...finalContactDetails?.sites[index].supplementaryQuestions?.parkingRestrictions,
      answer: reponseBool,
    };

    handleUpdateSupplementaryQuestions('parkingRestrictions', updatedParkingRestrictionReponse, index);
  };

  const handleUpdateParkingRestrictionDetail = (details: string, index: number) => {
    const updatedParkingRestrictionReponse: Api.IMeterUpgradeAnswer = {
      ...finalContactDetails?.sites[index].supplementaryQuestions?.parkingRestrictions,
      details: details,
    };

    handleUpdateSupplementaryQuestions('parkingRestrictions', updatedParkingRestrictionReponse, index);
  };

  const handleUpdateAccessRestrictionRadio = (response: string, index: number) => {
    const reponseBool = response === 'Yes' ? true : false;

    const updatedAccessRestrictionReponse: Api.IMeterUpgradeAnswer = {
      ...finalContactDetails?.sites[index].supplementaryQuestions?.accessRestrictions,
      answer: reponseBool,
    };

    handleUpdateSupplementaryQuestions('accessRestrictions', updatedAccessRestrictionReponse, index);
  };

  const handleUpdateAccessRestrictionDetail = (details: string, index: number) => {
    const updatedAccessRestrictionReponse: Api.IMeterUpgradeAnswer = {
      ...finalContactDetails?.sites[index].supplementaryQuestions?.accessRestrictions,
      details: details,
    };

    handleUpdateSupplementaryQuestions('accessRestrictions', updatedAccessRestrictionReponse, index);
  };

  const handleUpdateAccessAboveRadio = (response: string, index: number) => {
    const reponseBool = response === 'Yes' ? true : false;

    const updatedAccessAboveReponse: Api.IMeterUpgradeAnswer = {
      ...finalContactDetails?.sites[index].supplementaryQuestions?.accessAbove8ft,
      answer: reponseBool,
    };

    handleUpdateSupplementaryQuestions('accessAbove8ft', updatedAccessAboveReponse, index);
  };

  const handleUpdateAccessAboveDetail = (details: string, index: number) => {
    const updatedAccessAboveReponse: Api.IMeterUpgradeAnswer = {
      ...finalContactDetails?.sites[index].supplementaryQuestions?.accessAbove8ft,
      details: details,
    };

    handleUpdateSupplementaryQuestions('accessAbove8ft', updatedAccessAboveReponse, index);
  };

  const handleUpdateSiteVulnerabilitiesRadio = (response: string, index: number) => {
    const reponseBool = response === 'Yes' ? true : false;

    const updatedSiteVulnerabilitiesReponse: Api.IMeterUpgradeAnswer = {
      ...finalContactDetails?.sites[index].supplementaryQuestions?.vulnerabilitiesOnSite,
      answer: reponseBool,
    };

    handleUpdateSupplementaryQuestions('vulnerabilitiesOnSite', updatedSiteVulnerabilitiesReponse, index);
  };

  const handleUpdateSiteVulnerabilitiesDetail = (details: string, index: number) => {
    const updatedSiteVulnerabilitiesReponse: Api.IMeterUpgradeAnswer = {
      ...finalContactDetails?.sites[index].supplementaryQuestions?.vulnerabilitiesOnSite,
      details: details,
    };

    handleUpdateSupplementaryQuestions('vulnerabilitiesOnSite', updatedSiteVulnerabilitiesReponse, index);
  };

  const handleUpdateSupplementaryQuestions = (
    updatedField: string,
    updatedDetails: Api.IMeterUpgradeAnswer,
    index: number
  ) => {
    const updatedSupplementaryQuestions: Api.ISupplementaryQuestions = {
      ...finalContactDetails?.sites[index].supplementaryQuestions,
      [updatedField]: updatedDetails,
    };
    handleUpdateSiteDetailsLevel('supplementaryQuestions', updatedSupplementaryQuestions, index);
  };

  const handleSendExternalEmail = (preference: string) => {
    setEmailFormCopy(preference);

    if (preference === 'Yes') {
      setSendExternalEmail(true);
    } else {
      setSendExternalEmail(false);
    }
  };

  return (
    <>
      <MeterUpgradeWrapper>
        <FilterRowWrapper className='pb-4'>{pageTitle}</FilterRowWrapper>

        {selectedSites?.length && <div className='selectedAccountDisplay'>{`Acc - ${userDetail.essCustomerId}`}</div>}

        <div className='navigationBar'>{stepsBar}</div>
        <div>
          <h4 className='pl-4 pb-3' style={{ textDecoration: 'underline' }}>
            Confirm contact details for the sites
          </h4>

          <div className='row pageDescription'>
            <div className='col-12 col-xl-6'>You will be able to specify a contact for each site if required</div>
            <div className='col-12 col-xl-4 requiredFieldText'>* Required fields</div>
          </div>
        </div>

        <FormProvider {...methods}>
          <div ref={formRef}>
            <form>
              {finalContactDetails?.sites.map((item: Api.IMeterUpgradeSiteDetails, index: number) => (
                <SiteContactDetails
                  siteData={item}
                  index={index}
                  handleUpdateContactName={handleUpdateContactName}
                  handleUpdateContactNumber={handleUpdateContactNumber}
                  handleUpdateContactEmail={handleUpdateContactEmail}
                  handleUpdateParkingRestrictionRadio={handleUpdateParkingRestrictionRadio}
                  handleUpdateParkingRestrictionDetail={handleUpdateParkingRestrictionDetail}
                  handleUpdateAccessRestrictionRadio={handleUpdateAccessRestrictionRadio}
                  handleUpdateAccessRestrictionDetail={handleUpdateAccessRestrictionDetail}
                  handleUpdateAccessAboveRadio={handleUpdateAccessAboveRadio}
                  handleUpdateAccessAboveDetail={handleUpdateAccessAboveDetail}
                  handleUpdateSiteVulnerabilitiesRadio={handleUpdateSiteVulnerabilitiesRadio}
                  handleUpdateSiteVulnerabilitiesDetail={handleUpdateSiteVulnerabilitiesDetail}
                  submitButtonValidTouched={submitButtonValidTouched}
                ></SiteContactDetails>
              ))}

              <SitesWrapper>
                <YourInfoBlock />

                <div className='formRowWrapper withContactDetails'>
                  <div className='submitEmail d-flex dateField'>
                    <span className='pt-3'>Would you like a copy of this submission sent to your email address?*</span>
                    <div className='pl-4'>
                      <FormElement.Radio
                        id='page-submit-email'
                        className={''}
                        label={''}
                        name='submitEmail'
                        elements={['Yes', 'No']}
                        value={emailFormCopy}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handleSendExternalEmail(e.target.value);
                        }}
                      />
                    </div>
                  </div>

                  <div className='text-right pt-4 submitBtn'>
                    <ButtonPrimary
                      title='Submit'
                      className='submit-btn'
                      onClick={(event: any) => handleConfirmationStep(event)}
                      disabled={!submitButtonValid}
                    ></ButtonPrimary>
                  </div>
                </div>
              </SitesWrapper>
            </form>
          </div>
        </FormProvider>
        {isSubmitting && <Loading overlay nowait />}
      </MeterUpgradeWrapper>

      <SubmissionFailedModal
        show={showFailureModal}
        onHide={() => {
          setShowFailureModal(false);
        }}
      ></SubmissionFailedModal>
    </>
  );
};
